import React, { FC } from "react";
import _ from "lodash";
import {
  Chart as ChartJS,
  ArcElement,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar, Pie } from "react-chartjs-2";

import { QuestionType } from "typings/app/types";
import { AnswerOptions, FeedbackAnswer } from "typings/api/types";

import { StarRating } from "components/Question";
import { LdIcon, LdLabel } from "@emdgroup-liquid/liquid/dist/react";

import { chartColors } from "assets/styles/variables/colors";
import { ThumbsDownIcon, ThumbsUpIcon } from "components/Icons";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

interface Props {
  questionType: string;
  options: AnswerOptions[];
  answers?: FeedbackAnswer[];
}

const DEFAULT_LIKERT_SCALE: { [key: string]: string } = {
  "1": "strongly disagree",
  "2": "disagree",
  "3": "agree",
  "4": "strongly agree",
};

interface ThumbsResultProps {
  data: { yes: number; no: number };
}

const ThumbsResult: FC<ThumbsResultProps> = ({ data }) => {
  const { yes, no } = data;
  const total = yes + no;
  return (
    <div className="flex-row items-start flex gap-8">
      <LdLabel className="flex" position="right">
        <LdIcon>
          <ThumbsDownIcon />
        </LdIcon>
        <span>{_.round((no / total) * 100, 1)}%</span>
      </LdLabel>
      <LdLabel className="flex" position="right">
        <LdIcon>
          <ThumbsUpIcon />
        </LdIcon>
        <span>{_.round((yes / total) * 100, 1)}%</span>
      </LdLabel>
    </div>
  );
};

const AnswersSummary: React.FC<Props> = ({
  questionType,
  options,
  answers = [],
}) => {
  const values = _.groupBy(answers, "value");
  switch (questionType) {
    case QuestionType.LIKERT_SCALE:
      const chartOptions = {
        responsive: true,
        maintainAspectRatio: true,
        plugins: {
          decimation: {
            enabled: false,
          },
          legend: {
            position: "right" as const,
            maxWidth: 500,
          },
          title: {
            display: false,
            text: "Chart.js Bar Chart",
          },
        },
        // scales: {
        //   x: {
        //     stacked: true,
        //   },
        //   y: {
        //     stacked: true,
        //   },
        // },
        // indexAxis: "y" as const,
      };
      const data = {
        labels: [`Average: ${_.round(_.meanBy(answers, "value"), 1)}`],
        datasets: options.map(({ label, value }, index) => ({
          label,
          data: [values[`${value}`]?.length || 0.01],
          backgroundColor: Object.values(chartColors)[index],
        })),
      };
      return (
        <div className="flex max-w-lg">
          <Bar width={500} options={chartOptions} data={data} />
        </div>
      );
    case QuestionType.STAR_RATING:
      const avg = _.meanBy(answers, "value");
      return (
        <LdLabel position="left">
          <StarRating interactive={false} value={avg} />
          <span>{_.round(avg, 1)}</span>
        </LdLabel>
      );
    case QuestionType.THUMB_RATING:
      const thumbsData = {
        yes: values["true"]?.length || 0,
        no: values["false"]?.length || 0,
      };
      return <ThumbsResult data={thumbsData} />;
    case QuestionType.SELECT:
    case QuestionType.MULTI_SELECT:
      const selectedOptions = _.flatten(
        answers.map(({ value }) => value) as Array<string | string[]>
      );
      const groupedOptions = _.groupBy(
        selectedOptions.map((val) => ({ label: decodeURI(val) })),
        "label"
      );
      const pieData = {
        labels: Object.keys(groupedOptions),
        datasets: [
          {
            data: Object.values(groupedOptions).map((val) => val.length),
            backgroundColor: Object.values(chartColors).slice(
              0,
              Object.keys(groupedOptions).length
            ),
          },
        ],
      };
      return (
        <div className="flex max-w-xs">
          <Pie data={pieData} />
        </div>
      );
    default:
      return (
        <ul>
          {answers.map(({ value }) => (
            <li>{value}</li>
          ))}
        </ul>
      );
  }
};

export default AnswersSummary;
